/**
 * Capability-gated App Bridge
 * ---------------------------
 * Creates a bridge that enforces per-app publish/subscribe allowlists.
 * Apps not in the registry get no-op bridge APIs (cannot communicate).
 *
 * Usage:
 *   import { initAppBridge } from "./bridge/bridge";
 *   import REGISTRY from "./bridge/registry";
 *   initAppBridge(REGISTRY, { strict: false });
 *
 * Micro-apps then use:
 *   const api = window.AppBridge.register("myAppId", "myAppId-1");
 *   api.publish("USER_SELECTED", { id: 42 });
 */

/**
 * Initialize the bridge and expose it on window.AppBridge.
 * Call once in the wrapper before any micro-app scripts are injected.
 *
 * @param {Object} registry  — { appId: { publish: string[], subscribe: string[] } }
 * @param {Object} [opts]
 * @param {boolean} [opts.strict=false] — true = throw on violations, false = warn + ignore
 */
export function initAppBridge(registry, opts) {
  var bridge = createBridge(registry, opts);
  window.AppBridge = Object.freeze({
    register: bridge.register,
    destroyAll: bridge.destroyAll
  });
}

function createBridge(registry, opts) {
  var strict = opts && opts.strict === true;

  // type -> Set<{ instanceId, handler }>
  var subscribers = {};

  // instanceId -> [unsubscribeFn, ...]
  var instanceSubs = {};

  // instanceId -> appId (for destroy lookups)
  var instanceApp = {};

  // appId -> true — each appId can only register once (prevents impersonation)
  var registeredAppIds = {};

  // type -> latest payload (for replay to late subscribers)
  var latestByType = {};

  function violation(msg) {
    if (strict) throw new Error("[AppBridge] " + msg);
    console.warn("[AppBridge] " + msg);
  }

  function isAllowed(appId, direction, type) {
    var entry = registry[appId];
    if (!entry) return false;
    var list = entry[direction];
    return Array.isArray(list) && list.indexOf(type) !== -1;
  }

  /**
   * Payload guard — reject functions and DOM nodes to prevent
   * accidental closure leaks and DOM reference sharing between apps.
   */
  function safePayload(payload) {
    if (payload === null || payload === undefined) return true;
    if (typeof payload === "function") return false;
    if (typeof Node !== "undefined" && payload instanceof Node) return false;
    if (typeof payload === "object") {
      var keys = Object.keys(payload);
      for (var i = 0; i < keys.length; i++) {
        if (!safePayload(payload[keys[i]])) return false;
      }
    }
    return true;
  }

  function register(appId, instanceId) {
    // Block duplicate registration — the first caller wins.
    // Legitimate app registers at load time; any later impersonation attempt is rejected.
    if (registeredAppIds[appId]) {
      violation(appId + " is already registered — duplicate registration rejected");
      return {
        publish: function () {},
        subscribe: function () { return function () {}; },
        destroy: function () {}
      };
    }

    var entry = registry[appId];
    instanceApp[instanceId] = appId;
    instanceSubs[instanceId] = [];

    // If appId is not in registry, return a no-op API
    if (!entry) {
      return {
        publish: function (type) {
          violation(appId + " is not in the registry — publish(\"" + type + "\") ignored");
        },
        subscribe: function (type) {
          violation(appId + " is not in the registry — subscribe(\"" + type + "\") ignored");
          return function () {};
        },
        destroy: function () {
          delete instanceApp[instanceId];
          delete instanceSubs[instanceId];
        }
      };
    }

    registeredAppIds[appId] = true;

    function publish(type, payload) {
      if (!isAllowed(appId, "publish", type)) {
        violation(appId + " cannot publish \"" + type + "\"");
        return;
      }
      if (!safePayload(payload)) {
        violation(appId + " publish(\"" + type + "\"): payload contains function or DOM node");
        return;
      }
      latestByType[type] = payload;
      var set = subscribers[type];
      if (!set) return;
      // Copy to array to avoid mutation during iteration
      var list = [];
      set.forEach(function (s) { list.push(s); });
      for (var i = 0; i < list.length; i++) {
        try {
          list[i].handler(payload);
        } catch (e) {
          console.error("[AppBridge] subscriber error in " + list[i].instanceId, e);
        }
      }
    }

    function subscribe(type, handler) {
      if (typeof handler !== "function") {
        violation("subscribe handler must be a function");
        return function () {};
      }
      if (!isAllowed(appId, "subscribe", type)) {
        violation(appId + " cannot subscribe to \"" + type + "\"");
        return function () {};
      }

      if (!subscribers[type]) subscribers[type] = new Set();
      var record = { instanceId: instanceId, handler: handler };
      subscribers[type].add(record);

      var removed = false;
      var unsub = function () {
        if (removed) return;
        removed = true;
        var set = subscribers[type];
        if (set) {
          set.delete(record);
          if (set.size === 0) delete subscribers[type];
        }
      };

      instanceSubs[instanceId].push(unsub);

      // Replay latest payload if available (handles async script load order)
      if (type in latestByType) {
        try {
          handler(latestByType[type]);
        } catch (e) {
          console.error("[AppBridge] replay error in " + instanceId, e);
        }
      }

      return unsub;
    }

    function destroy() {
      var list = instanceSubs[instanceId];
      if (list) {
        for (var i = 0; i < list.length; i++) list[i]();
      }
      delete instanceSubs[instanceId];
      delete instanceApp[instanceId];
      delete registeredAppIds[appId]; // release slot so app can re-register after remount
    }

    return { publish: publish, subscribe: subscribe, destroy: destroy };
  }

  function destroyInstance(instanceId) {
    var appId = instanceApp[instanceId];
    if (!appId) return;
    var list = instanceSubs[instanceId];
    if (list) {
      for (var i = 0; i < list.length; i++) list[i]();
    }
    delete instanceSubs[instanceId];
    delete instanceApp[instanceId];
    delete registeredAppIds[appId]; // release slot
  }

  function destroyAll() {
    var ids = Object.keys(instanceApp);
    for (var i = 0; i < ids.length; i++) {
      destroyInstance(ids[i]);
    }
    // Clear replay cache so stale payloads don't leak across navigations
    var types = Object.keys(latestByType);
    for (var j = 0; j < types.length; j++) {
      delete latestByType[types[j]];
    }
  }

  return { register: register, destroyInstance: destroyInstance, destroyAll: destroyAll };
}
